import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import StatTile from '../shared/StatTile';
import { FiFolder, FiTrendingUp, FiDollarSign, FiTarget, FiUserCheck, FiAlertCircle, FiArrowUp, FiArrowDown } from 'react-icons/fi';

const fmt = (n, decimals = 0) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtW = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M MT`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K MT`;
  return `${v.toFixed(1)} MT`;
};

const YoY = ({ current, previous }) => {
  if (!previous || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  const up  = pct >= 0;
  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-semibold ${up ? 'tw-text-emerald-600' : 'tw-text-red-500'}`}
    >
      {up ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};
YoY.propTypes = {
  current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previous: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const SalesSummaryCards = ({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="tw-p-4 tw-border tw-border-gray-100">
            <div className="tw-flex tw-justify-center tw-items-center tw-py-6">
              <LoadingSpinner size="sm" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
        <p className="tw-text-red-600 tw-text-sm">Error loading summary metrics</p>
      </Card>
    );
  }

  const d = data || {};
  const winRateGood = d.win_rate >= 50;
  const cards = [
    {
      label:      'Total Projects',
      value:      fmt(d.total_projects),
      sub:        `${fmt(d.from_lead_count)} from lead · ${fmt(d.direct_count)} direct`,
      yoyCurrent: d.total_projects,
      yoyPrev:    d.prev_total_projects,
      icon:       FiFolder,
      theme:      'blue'
    },
    {
      label:      'Pipeline Value',
      value:      fmtM(d.pipeline_value),
      sub:        'Active projects only',
      yoyCurrent: d.pipeline_value,
      yoyPrev:    d.prev_pipeline_value,
      icon:       FiDollarSign,
      theme:      'indigo',
      tooltip:    'Active projects only (lost deals excluded)'
    },
    {
      label:      'Pipeline Weight',
      value:      fmtW(d.pipeline_weight),
      sub:        'Active projects only',
      yoyCurrent: d.pipeline_weight,
      yoyPrev:    d.prev_pipeline_weight,
      icon:       FiTrendingUp,
      theme:      'cyan',
      tooltip:    'Active projects only (lost deals excluded)'
    },
    {
      label:      'Contract Value',
      value:      fmtM(d.contract_value),
      sub:        'Signed contracts',
      yoyCurrent: d.contract_value,
      yoyPrev:    d.prev_contract_value,
      icon:       FiTarget,
      theme:      'green'
    },
    {
      label:          'Win Rate',
      value:          `${d.win_rate ?? 0}%`,
      sub:            (
        <span className={winRateGood ? 'tw-text-emerald-500' : 'tw-text-amber-500'}>
          Quotation → Job
        </span>
      ),
      yoyCurrent:     d.win_rate,
      yoyPrev:        d.prev_win_rate,
      icon:           FiUserCheck,
      theme:          winRateGood ? 'green' : 'amber',
      valueClassName: winRateGood ? 'tw-text-emerald-600' : 'tw-text-amber-600'
    },
    {
      label:      'Lost Projects',
      value:      fmt(d.lost_count),
      sub:        'Cancelled / lost',
      yoyCurrent: d.lost_count,
      yoyPrev:    d.prev_lost_count,
      icon:       FiAlertCircle,
      theme:      'rose'
    }
  ];

  return (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4">
      {cards.map(({ yoyCurrent, yoyPrev, ...c }) => (
        <StatTile
          key={c.label}
          {...c}
          trend={<YoY current={yoyCurrent} previous={yoyPrev} />}
        />
      ))}
    </div>
  );
};

SalesSummaryCards.propTypes = {
  data:    PropTypes.object,
  loading: PropTypes.bool,
  error:   PropTypes.any
};
SalesSummaryCards.defaultProps = { data: null, loading: false, error: null };

export default SalesSummaryCards;
